/* SCREEN — LANDING (upload). Exact port of code.txt Landing + UploadZone + FileRow.
   Files are parsed and family-detected IN THE BROWSER for the live slot preview; the
   raw files are still uploaded and re-detected server-side (source of truth). */

import Papa from "papaparse";
import { detectFile } from "@agf/analyzer";
import { Card, Pill, BtnPrimary, Lbl, Check, T, MONO, FONT, escapeHtml } from "../ui.js";
import { AppLogo, AioLabel, AioSparkle } from "../ui.js";

const ZONE1_FILES = [
  { name: "Queries", role: "slot", kind: "queries", req: true, note: "search keywords with clicks & position" },
  { name: "Pages", role: "slot", kind: "tradPages", req: false, note: "improves page matching accuracy" },
  { name: "Chart", role: "skip" },
  { name: "Countries", role: "skip" },
  { name: "Devices", role: "skip" },
  { name: "Filters", role: "skip" },
  { name: "Search appearance", role: "skip" },
];
const ZONE2_FILES = [
  { name: "Pages", role: "slot", kind: "aiPages", req: true, note: "pages with Generative AI impressions" },
  { name: "Chart", role: "slot", kind: "aiDaily", req: false, note: "unlocks the AI visibility trend" },
  { name: "Countries", role: "skip" },
  { name: "Devices", role: "skip" },
  { name: "Filters", role: "skip" },
];

function FileRow(f, state, received, color, soft) {
  const isSlot = f.role === "slot";
  const ok = isSlot ? !!state : received;
  return `
    <div style="display:flex;align-items:center;gap:10px;padding:6px 10px;border-radius:9px;border:1px solid ${ok ? T.green : T.lineSoft};background:${ok ? T.greenSoft : T.surface};margin-bottom:5px">
      <div style="width:18px;height:18px;border-radius:6px;background:${ok ? T.green : T.bg};border:${ok ? "none" : `1px solid ${T.line}`};display:flex;align-items:center;justify-content:center;flex-shrink:0">
        ${ok ? Check("#fff") : `<span style="width:5px;height:5px;border-radius:50%;background:${T.line};display:inline-block"></span>`}
      </div>
      <span style="font-family:${MONO};font-size:11.5px;font-weight:700;color:${isSlot ? color : T.muted};flex-shrink:0">${f.name}.csv</span>
      <span style="font-size:10.5px;color:${T.muted};flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
        ${isSlot ? (state ? `${escapeHtml(state.name.split("/").pop())} · ${fmtRows(state.rows)} rows` : f.note) : (received ? "received" : "")}
      </span>
      ${isSlot && !state && f.req ? Pill(color, soft, "REQUIRED") : ""}
    </div>`;
}
const fmtRows = n => (n >= 1e3 ? (n / 1e3).toFixed(1) + "K" : String(n));

const SERP_LOCATIONS = [[2840, "United States"], [2356, "India"], [2826, "United Kingdom"], [2124, "Canada"], [2036, "Australia"], [2276, "Germany"], [2702, "Singapore"], [2784, "UAE"]];
const SERP_LANGS = [["en", "English"], ["hi", "Hindi"], ["es", "Spanish"], ["de", "German"], ["fr", "French"], ["ar", "Arabic"]];

export function renderSetup(app, { mode, hasDfs, draft, actions }) {
  /* the caller owns this object, so uploads survive a repaint (account popup, etc.) */
  const st = draft;

  app.innerHTML = `
    <div style="min-height:100vh;background:${T.bg};display:flex;align-items:center;justify-content:center;padding:24px;font-family:${FONT}">
      <div style="width:100%;max-width:820px">
        <div style="display:flex;align-items:center;gap:10px;justify-content:center;margin-bottom:26px">
          ${AppLogo(30)}
          <div>
            <div style="font-size:14px;font-weight:800;letter-spacing:-0.3px"><span style="color:${T.violet}">AIO Gap</span> Finder</div>
            <div style="font-size:9.5px;color:${T.muted};font-weight:600;letter-spacing:0.5px;text-transform:uppercase">Free tool by Botpresso</div>
          </div>
        </div>
        ${Card(`
          <button id="btn-back" style="display:inline-flex;align-items:center;gap:6px;border:none;background:transparent;color:${T.sub};font-family:${FONT};font-size:12px;font-weight:700;cursor:pointer;padding:0;margin-bottom:10px">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            Dashboard
          </button>
          <div style="display:flex;align-items:center;gap:10px;margin-bottom:8px">
            <h1 style="font-size:22px;font-weight:800;letter-spacing:-0.5px;margin:0;color:${T.ink}">New analysis</h1>
            ${mode === "serp"
              ? Pill(T.magenta, T.magentaSoft, AioLabel("WITH SERP", 10))
              : Pill(T.violet, T.violetSoft, "GSC ONLY")}
          </div>
          <p style="font-size:13.5px;color:${T.sub};margin:0 0 16px;line-height:1.55">Drop your Search Console export folders below — every file is auto-mapped to its type.</p>
          ${mode === "serp" ? `
            <div style="background:${T.magentaSoft};border:1px solid rgba(245,15,175,0.2);border-radius:12px;padding:12px 14px;margin-bottom:14px">
              ${Lbl(AioLabel("SERP check settings (DataForSEO)", 10), `color:${T.magenta}`)}
              <div style="display:flex;gap:10px;margin-top:8px;flex-wrap:wrap">
                <label style="font-size:11px;color:${T.sub};font-weight:600">Location<br/>
                  <select id="serp-location" style="margin-top:3px;padding:7px 9px;border-radius:9px;border:1px solid ${T.line};background:${T.surface};font-family:${FONT};font-size:12px;color:${T.ink}">
                    ${SERP_LOCATIONS.map(([code, name]) => `<option value="${code}">${name}</option>`).join("")}
                  </select>
                </label>
                <label style="font-size:11px;color:${T.sub};font-weight:600">Language<br/>
                  <select id="serp-language" style="margin-top:3px;padding:7px 9px;border-radius:9px;border:1px solid ${T.line};background:${T.surface};font-family:${FONT};font-size:12px;color:${T.ink}">
                    ${SERP_LANGS.map(([code, name]) => `<option value="${code}">${name}</option>`).join("")}
                  </select>
                </label>
                <label style="font-size:11px;color:${T.sub};font-weight:600">Device<br/>
                  <select id="serp-device" style="margin-top:3px;padding:7px 9px;border-radius:9px;border:1px solid ${T.line};background:${T.surface};font-family:${FONT};font-size:12px;color:${T.ink}">
                    <option value="desktop">Desktop</option><option value="mobile">Mobile</option>
                  </select>
                </label>
                <label style="font-size:11px;color:${T.sub};font-weight:600">Keywords to check<br/>
                  <select id="serp-keywords" style="margin-top:3px;padding:7px 9px;border-radius:9px;border:1px solid ${T.line};background:${T.surface};font-family:${FONT};font-size:12px;color:${T.ink}">
                    <option value="100">Top 100 (~$0.06)</option>
                    <option value="250">Top 250 (~$0.15)</option>
                    <option value="500">Top 500 (~$0.30)</option>
                    <option value="1000">Top 1000 (~$0.60)</option>
                  </select>
                </label>
              </div>
              <div style="font-size:10.5px;color:${T.muted};margin-top:8px">Keywords are checked in impression order; the rest show “not checked”. <button id="btn-account" style="border:none;background:transparent;color:${T.magenta};font-family:${FONT};font-size:10.5px;font-weight:700;cursor:pointer;padding:0;text-decoration:underline">API credentials</button></div>
            </div>` : ""}
          <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:12px;align-items:start">
            ${uploadZone("1", T.violet, T.violetSoft, "Performance → Search results", '"…-Performance-on-Search-…" folder', false, ZONE1_FILES, "trad")}
            ${uploadZone("2", T.magenta, T.magentaSoft, "Performance → Generative AI", '"…-Generative-AI-Features-…" folder', true, ZONE2_FILES, "ai")}
          </div>
          <div id="setup-errors"></div>
          <div id="serp-status" style="display:none;font-size:11.5px;color:${T.magenta};font-weight:600;margin:2px 2px 6px"></div>
          ${BtnPrimary(`Find My AI Opportunities
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>`,
          { disabled: true, attrs: 'id="btn-go"', style: "width:100%;justify-content:center;margin-top:10px" })}
          <div style="margin-top:12px">
            <button id="btn-help" style="border:none;background:transparent;color:${T.sub};font-family:${FONT};font-size:12px;font-weight:600;cursor:pointer;padding:0">Where do I get these files?</button>
          </div>
          <div id="setup-help" style="display:none;margin-top:12px;background:${T.bg};border:1px solid ${T.lineSoft};border-radius:11px;padding:12px 14px;font-size:12px;color:${T.sub};line-height:1.7">
            <b style="color:${T.ink}">1.</b> In Search Console open <b>Performance → Search results</b> → <b>Export</b>. You get a folder like <span style="font-family:${MONO};font-size:10.5px">https___yoursite_-Performance-on-Search-…</span> — drop the whole folder here (we use its Queries and Pages files).<br/>
            <b style="color:${T.ink}">2.</b> Open <b>Performance → Generative AI</b> → <b>Export</b>. You get <span style="font-family:${MONO};font-size:10.5px">…-Performance-on-Search-Generative-AI-Features-…</span> — drop that folder too (we use its Pages and Chart files).<br/>
            Countries, Devices, Filters and Search appearance files are skipped automatically. Use the same date range for both exports so the comparison is fair.
          </div>`,
        { style: "padding:28px" })}
      </div>
    </div>`;

  function uploadZone(num, color, soft, title, subtitle, sparkle, files, zone) {
    return `
      <div style="border:1px solid ${T.lineSoft};background:${T.bg};border-radius:14px;padding:14px 14px 9px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
          <span style="width:20px;height:20px;border-radius:7px;background:${soft};color:${color};font-family:${MONO};font-weight:800;font-size:11.5px;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0">${num}</span>
          <span style="font-weight:800;font-size:13.5px;color:${T.ink};display:inline-flex;align-items:center;gap:5px">${sparkle ? AioSparkle(12) : ""}${title}</span>
        </div>
        <div data-dropzone="${zone}" style="border:1.5px dashed ${T.line};background:${T.surface};border-radius:11px;padding:16px 14px;text-align:center;cursor:pointer;margin-bottom:10px;transition:all .15s">
          <div style="font-weight:700;font-size:12.5px;color:${T.ink}">Drop the folder or files here</div>
          <div style="font-size:10.5px;color:${T.muted};margin-top:2px">${subtitle} · or click to browse</div>
          <input data-picker="${zone}" type="file" accept=".csv,text/csv" multiple style="display:none"/>
        </div>
        <div data-files="${zone}">
          ${files.map(f => FileRow(f, f.kind ? st.slots[f.kind] : null, st.extras[zone].includes(f.name.toLowerCase()), color, soft)).join("")}
        </div>
      </div>`;
  }

  /* ---------- ingest preview logic (prototype parity) ---------- */
  async function parseCsvFile(file) {
    return new Promise((resolve, reject) => {
      Papa.parse(file, { header: true, skipEmptyLines: true, complete: resolve, error: reject });
    });
  }

  async function ingest(entries, famHint) {
    // entries: [{file, path}] — path keeps the export FOLDER name, which distinguishes
    // the Search results folder from the Generative AI folder when inner names collide.
    const errs = [];
    for (const en of entries) {
      const f = en.file || en;
      const label = en.path || f.name;
      if (!/\.csv$/i.test(f.name)) continue;
      try {
        const res = await parseCsvFile(f);
        const det = detectFile(res.data, res.meta.fields || [], label, famHint);
        if (det.kind === "extra") {
          const base = f.name.replace(/\.csv$/i, "").replace(/[_-]+/g, " ").trim().toLowerCase();
          const zone = famHint || "trad";
          st.extras[zone] = [...new Set([...st.extras[zone], base])];
          continue;
        }
        st.slots[det.kind] = { name: label, rows: det.data.length, data: det.data, file: f };
      } catch {
        errs.push({ name: label, hard: true, msg: "Couldn't read this file — export it as CSV and try again." });
      }
    }
    st.errors = errs;
    redraw();
  }

  async function collectDropped(dt) {
    const out = [];
    const walk = entry => new Promise(resolve => {
      if (!entry) return resolve();
      if (entry.isFile) entry.file(f => { out.push({ file: f, path: entry.fullPath.replace(/^\//, "") }); resolve(); }, () => resolve());
      else if (entry.isDirectory) {
        const reader = entry.createReader();
        const readAll = acc => reader.readEntries(async batch => {
          if (!batch.length) { for (const e of acc) await walk(e); resolve(); }
          else readAll(acc.concat([...batch]));
        }, () => resolve());
        readAll([]);
      } else resolve();
    });
    const items = dt.items ? [...dt.items] : [];
    const entries = items.map(it => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null)).filter(Boolean);
    if (entries.length) { for (const e of entries) await walk(e); return out; }
    return [...dt.files].map(f => ({ file: f, path: f.name }));
  }

  function redraw() {
    for (const [zone, sel] of [["trad", ZONE1_FILES], ["ai", ZONE2_FILES]]) {
      app.querySelector(`[data-files="${zone}"]`).innerHTML = sel.map(f =>
        FileRow(f, f.kind ? st.slots[f.kind] : null, st.extras[zone].includes(f.name.toLowerCase()), zone === "trad" ? T.violet : T.magenta, zone === "trad" ? T.violetSoft : T.magentaSoft)).join("");
    }
    app.querySelector("#setup-errors").innerHTML = st.errors.map(er =>
      `<div style="font-size:11.5px;color:${er.hard ? T.red : T.muted};margin:4px 2px"><b>${escapeHtml(er.name)}</b> — ${er.msg}</div>`).join("")
      + (st.submitError ? `<div style="font-size:11.5px;color:${T.red};margin:4px 2px">${escapeHtml(st.submitError)}</div>` : "");
    const ready = st.slots.queries && st.slots.aiPages;
    const btn = app.querySelector("#btn-go");
    btn.disabled = !ready;
    /* the primary button bakes its colours in at render time, so repaint them here —
       otherwise it stays grey and looks dead even once both required files are in */
    btn.style.background = ready ? T.violet : T.lineSoft;
    btn.style.color = ready ? "#fff" : T.muted;
    btn.style.cursor = ready ? "pointer" : "default";
  }

  function wireZone(zone) {
    const drop = app.querySelector(`[data-dropzone="${zone}"]`);
    const picker = app.querySelector(`[data-picker="${zone}"]`);
    const color = zone === "ai" ? T.magenta : T.violet;
    const soft = zone === "ai" ? T.magentaSoft : T.violetSoft;
    drop.addEventListener("click", () => picker.click());
    drop.addEventListener("dragover", e => { e.preventDefault(); drop.style.borderColor = color; drop.style.background = soft; });
    drop.addEventListener("dragleave", () => { drop.style.borderColor = T.line; drop.style.background = T.surface; });
    drop.addEventListener("drop", async e => {
      e.preventDefault();
      drop.style.borderColor = T.line;
      drop.style.background = T.surface;
      ingest(await collectDropped(e.dataTransfer), zone);
    });
    picker.addEventListener("change", () => {
      ingest([...picker.files].map(f => ({ file: f, path: f.webkitRelativePath || f.name })), zone);
      picker.value = "";
    });
  }
  wireZone("trad");
  wireZone("ai");

  app.querySelector("#btn-back").addEventListener("click", actions.onBack);
  if (mode === "serp") app.querySelector("#btn-account").addEventListener("click", actions.onAccount);
  app.querySelector("#btn-help").addEventListener("click", () => {
    const el = app.querySelector("#setup-help");
    el.style.display = el.style.display === "none" ? "block" : "none";
  });

  app.querySelector("#btn-go").addEventListener("click", async () => {
    const btn = app.querySelector("#btn-go");
    if (btn.disabled) return;
    /* SERP mode needs DataForSEO credentials — ask for them here instead of failing */
    if (mode === "serp" && !hasDfs) { actions.onAccount(true); return; }
    st.submitError = null;
    btn.disabled = true;
    try {
      const fd = new FormData();
      fd.append("mode", mode);
      if (mode === "serp") {
        fd.append("location_code", app.querySelector("#serp-location").value);
        fd.append("language_code", app.querySelector("#serp-language").value);
        fd.append("device", app.querySelector("#serp-device").value);
        fd.append("keyword_cap", app.querySelector("#serp-keywords").value);
      }
      /* the raw files ride along; the server re-runs detection as source of truth */
      const zoneByKind = { queries: "trad", tradPages: "trad", aiPages: "ai", aiDaily: "ai" };
      for (const kind of Object.keys(zoneByKind)) {
        const slot = st.slots[kind];
        if (slot) fd.append(zoneByKind[kind], slot.file, slot.name);
      }
      await actions.onRun(fd);
    } catch (err) {
      st.submitError = err.message;
      btn.disabled = false;
      redraw(); // shows the error AND restores the button's enabled look
    }
  });

  /* Reflect whatever the draft already holds — on a repaint (account popup, credentials
     saved) the files are still there, so the button must come back enabled. */
  redraw();
}
